<template>
  <div class="p-8 bg-[#F8FAFC] min-h-screen">
    <!-- Header -->
    <header class="flex lg:flex-row justify-between items-start lg:items-center mb-8 gap-6">
      <div class="flex items-center gap-4">
        <div>
          <h1 class="text-2xl font-black text-gray-900 uppercase tracking-tight">
            Back Office <span class="text-purple-600">Vue d'ensemble</span>
          </h1>
          <p class="text-sm text-gray-500 font-medium italic">
            Supervision des files d'attente
          </p>
        </div>
      </div>

      <div class="flex items-center gap-4">
        <!-- WebSocket Status -->
        <div class="text-right">
          <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Statut Connexion</p>
          <p class="text-sm font-bold flex items-center gap-2" :class="isSocketConnected ? 'text-green-500' : 'text-red-500'">
            <span class="w-2 h-2 rounded-full" :class="isSocketConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'"></span>
            {{ isSocketConnected ? 'Temps Réel Actif' : 'Reconnexion...' }}
          </p>
        </div>
      </div>
    </header>

    <!-- Total Waiting Card -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-purple-600 to-blue-600 rounded-[2.5rem] p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[10px] font-black uppercase tracking-widest opacity-80">Total en Attente</p>
            <h2 class="text-6xl font-black tabular-nums mt-2">{{ totalWaiting }}</h2>
            <p class="text-sm font-medium opacity-90 mt-1">Clients dans toutes les files</p>
          </div>
          <div class="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
            <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Queue Stats Grid -->
    <div class="grid md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
      <div
        v-for="queue in queueStats"
        :key="queue.queueId"
        class="bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
        <div class="flex items-start justify-between mb-4">
          <div class="flex-1">
            <h3 class="text-xl font-black text-gray-800 uppercase">{{ queue.queueName }}</h3>
            <p class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">{{ queue.queuePrefix }}</p>
          </div>
          <div class="bg-blue-600 text-white text-2xl font-black px-4 py-2 rounded-2xl tabular-nums">
            {{ queue.waitingCount }}
          </div>
        </div>

        <!-- Assigned Counters -->
        <div class="mt-6">
          <p class="text-[9px] font-black uppercase tracking-widest text-gray-400 mb-3">Caisses Assignées</p>
          <div class="space-y-2">
            <div
              v-for="counter in queue.counters"
              :key="counter.id"
              class="px-3 py-2 bg-gray-50 rounded-xl text-sm font-bold text-gray-700 border border-gray-100">
              {{ counter.name }}
            </div>
            <div v-if="queue.counters.length === 0" class="text-xs text-gray-400 italic">
              Aucune caisse assignée
            </div>
          </div>
        </div>

        <!-- Summary Text -->
        <div v-if="queue.waitingCount > 0" class="mt-6 pt-4 border-t border-gray-100">
          <p class="text-sm font-bold text-gray-600">
            {{ queue.waitingCount }} {{ queue.waitingCount > 1 ? 'Tickets' : 'Ticket' }}
            <span class="text-gray-800">{{ queue.queueName }}</span>
            <span v-if="queue.counters.length > 0" class="text-gray-500">
              - {{ queue.counters.map(c => c.name).join(', ') }}
            </span>
          </p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="queueStats.length === 0" class="col-span-full">
        <div class="bg-blue-50 border-2 border-blue-200 rounded-[2rem] p-8 flex flex-col items-center text-center">
          <div class="w-16 h-16 bg-blue-200 rounded-full flex items-center justify-center text-blue-600 mb-4">
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-width="3" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h2 class="text-xl font-black text-blue-900 uppercase">Aucune file d'attente</h2>
          <p class="text-blue-700 text-sm font-medium mt-1">Aucune donnée disponible</p>
        </div>
      </div>
    </div>

    <!-- Recent Called Tickets -->
    <div class="bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-sm">
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Derniers Tickets Appelés</h3>
        <span class="bg-purple-600 text-white text-[10px] font-black px-2 py-1 rounded-md">{{ recentCalled.length }}</span>
      </div>

      <div class="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        <div
          v-for="ticket in recentCalled"
          :key="ticket.id"
          class="p-4 rounded-2xl border border-gray-50 bg-gray-50/50 flex justify-between items-center">
          <div class="flex-1">
            <p class="text-sm font-black text-gray-800">{{ ticket.number }}</p>
            <p class="text-[9px] font-bold text-gray-400 uppercase">{{ ticket.queueName }}</p>
          </div>
          <div class="flex items-center gap-3">
            <span class="text-xs font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-lg">
              {{ ticket.counterName }}
            </span>
            <span class="text-[10px] font-bold text-gray-400">
              {{ formatTime(ticket.calledAt) }}
            </span>
          </div>
        </div>

        <div v-if="recentCalled.length === 0" class="text-center py-10 text-gray-300 font-bold uppercase text-[10px]">
          Aucun ticket appelé récemment
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div v-if="notification"
         class="fixed bottom-8 right-8 bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 z-50 animate-slide-in">
      <span class="text-2xl">{{ notification.icon }}</span>
      <div>
        <p class="font-black text-sm">{{ notification.title }}</p>
        <p class="text-xs opacity-80">{{ notification.message }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { io } from 'socket.io-client';
import axios from 'axios';

// API Configuration
const API_URL = 'https://api.datafuse.fr/api/agency';
const WS_URL = 'https://api.datafuse.fr';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.authorization = `Bearer ${token}`;
  }
  return config;
});

// User Info
const agencyId = localStorage.getItem('agencyId');
const token = localStorage.getItem('token');

// State
const totalWaiting = ref(0);
const queueStats = ref([]);
const recentCalled = ref([]);
const notification = ref(null);
const isSocketConnected = ref(false);

// WebSocket
let socket = null;
let refreshInterval = null;

// Initialize
onMounted(() => {
  fetchStats();
  initWebSocket();

  // Auto-refresh every 10 seconds
  refreshInterval = setInterval(() => {
    fetchStats();
  }, 10000);
});

onUnmounted(() => {
  if (socket) {
    socket.disconnect();
  }
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
});

// Fetch Back Office Stats
const fetchStats = async () => {
  try {
    const response = await api.get(`/back-office-stats/${agencyId}`);
    totalWaiting.value = response.data.totalWaiting;
    queueStats.value = response.data.queueStats;
    recentCalled.value = response.data.recentCalled;
    console.log('✅ Stats Back Office récupérées');
  } catch (error) {
    console.error('❌ Erreur récupération stats:', error);
    showNotification('❌', 'Erreur', 'Impossible de charger les stats');
  }
};

// Format time (HH:MM)
const formatTime = (dateString) => {
  if (!dateString) return '--:--';
  const date = new Date(dateString);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

// WebSocket Initialization
const initWebSocket = () => {
  if (!token) {
    console.error('❌ Token manquant pour WebSocket');
    return;
  }

  console.log('🔌 Initialisation WebSocket...');

  try {
    socket = io(WS_URL, {
      query: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    socket.on('connect', () => {
      console.log('✅ WebSocket connecté - ID:', socket.id);
      isSocketConnected.value = true;
      showNotification('✅', 'Connecté', 'WebSocket actif');
    });

    socket.on('connect_error', (error) => {
      console.error('❌ WS CONNECTION ERROR:', error.message);
      isSocketConnected.value = false;
    });

    socket.on('ticket_called', () => {
      console.log('📞 Ticket appelé - Refresh stats');
      fetchStats();
    });

    socket.on('queue_updated', () => {
      console.log('🔄 File mise à jour - Refresh stats');
      fetchStats();
    });

    socket.on('new_ticket_waiting', () => {
      console.log('🆕 Nouveau ticket - Refresh stats');
      fetchStats();
    });

    socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket déconnecté:', reason);
      isSocketConnected.value = false;
    });

    socket.on('reconnect', () => {
      console.log('🔄 WebSocket reconnecté');
      isSocketConnected.value = true;
      fetchStats();
    });

  } catch (error) {
    console.error('❌ Erreur initialisation WebSocket:', error);
  }
};

// Show Notification
const showNotification = (icon, title, message) => {
  notification.value = { icon, title, message };
  setTimeout(() => {
    notification.value = null;
  }, 3000);
};
</script>

<style scoped>
@keyframes slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease-out;
}
</style>
